import { useState } from 'react';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/Toast';
import { adSettingsApi, type AdSettings } from '../../api/adSettingsApi';
import { useIsAgencyStaff } from '../../hooks';
import {
  OVERRIDE_WORD,
  changeRulesInput,
  overrideTyped,
  type ChangeRulesDraft,
} from '../../utils/adSettingsForm';
import { useAdSettings } from './useAdSettings';
import styles from './PaidSettings.module.css';

const draftOf = (s: AdSettings): ChangeRulesDraft => ({
  baselineDate: s.baselineDate ?? '',
  minCallsForChange: String(s.minCallsForChange),
  changeCooldownDays: String(s.changeCooldownDays),
});

/**
 * How Jenny recommends changes: the fewest calls before a change, the wait after one, and the day the campaigns were
 * rebuilt. Set by the agency. In Settings everyone reads them and agency staff type OVERRIDE to edit
 * (`unlock="typed"`); the Command Center, where only agency staff are, edits them directly (`unlock="open"`).
 */
export function ChangeRulesCard({
  locationId,
  unlock,
}: {
  locationId: string;
  unlock: 'typed' | 'open';
}) {
  const toast = useToast();
  const isAgency = useIsAgencyStaff();
  const { data, isLoading, save } = useAdSettings(locationId);
  const [typed, setTyped] = useState('');
  const [draft, setDraft] = useState<ChangeRulesDraft | null>(null);

  if (isLoading || !data)
    return (
      <Card title="How Jenny recommends changes">
        <Spinner size="sm" />
      </Card>
    );

  const editing = isAgency && (unlock === 'open' || draft !== null);
  const current = draft ?? draftOf(data);
  const submit = () => {
    const result = changeRulesInput(current);
    if (!result.ok) {
      toast.error(result.error);
      return;
    }
    save.mutate(() => adSettingsApi.saveChangeRules(locationId, result.input), {
      onSuccess: () => {
        setDraft(null);
        setTyped('');
      },
    });
  };

  return (
    <Card
      title="How Jenny recommends changes"
      subtitle={unlock === 'typed' ? 'Set by your agency' : undefined}
    >
      {editing ? (
        <>
          <div className={styles.row}>
            <Input
              label="Fewest calls (30s+) before a change"
              inputMode="numeric"
              value={current.minCallsForChange}
              onChange={(e) => setDraft({ ...current, minCallsForChange: e.target.value })}
            />
            <Input
              label="Wait after a change (days)"
              inputMode="numeric"
              value={current.changeCooldownDays}
              onChange={(e) => setDraft({ ...current, changeCooldownDays: e.target.value })}
            />
            <Input
              type="date"
              label="Campaigns rebuilt on"
              value={current.baselineDate}
              onChange={(e) => setDraft({ ...current, baselineDate: e.target.value })}
            />
          </div>
          <p className={styles.hint}>
            Leave the rebuild date empty when there was none. Comparisons across it are marked not
            like-for-like.
          </p>
          <div className={styles.actions}>
            {unlock === 'typed' && (
              <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>
                Cancel
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={submit}
              disabled={save.isPending || draft === null}
            >
              Save rules
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.tiles}>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>Fewest calls (30s+) before a change</span>
              <span className={styles.tileValue}>{data.minCallsForChange}</span>
            </div>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>Wait after a change</span>
              <span className={styles.tileValue}>{data.changeCooldownDays} days</span>
            </div>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>Campaigns rebuilt on</span>
              <span className={styles.tileValue}>{data.baselineDate ?? 'No rebuild'}</span>
            </div>
          </div>
          {isAgency && (
            <div className={styles.agency}>
              <span className={styles.agencyTag}>Agency only</span>
              <Input
                label={`Type ${OVERRIDE_WORD} to edit`}
                value={typed}
                placeholder={OVERRIDE_WORD}
                onChange={(e) => setTyped(e.target.value)}
              />
              <Button
                variant="secondary"
                size="sm"
                disabled={!overrideTyped(typed)}
                onClick={() => setDraft(draftOf(data))}
              >
                Unlock
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
